import * as React from 'react';
import {Trans} from '@deviceinsight/ng-ui-scale-lib/api';
import {debounce} from 'lodash';
import {PaginationSettings, Pageable, SortDirection} from '@deviceinsight/ng-ui-api-client';
import {ReactableColumnDef, ReactableRowDef} from '@deviceinsight/ng-ui-components';
import {Reactable} from '@deviceinsight/ng-ui-components';
import Search from './Search';
import {SearchConfig} from './types';
type Props<T> = {
	searchContext: string;
	searchConfig: SearchConfig<T>;
	columnDefinitions: Array<ReactableColumnDef<T>>;
	pageSize: number;
	uniqueItemKey: (arg0: T) => any;
	onUpdate?: (query: string, searchResult: Pageable<T>) => void;
	updateTrigger?: any | null | undefined;
	withSync?: {
		itemsToSync: number;
		expectedTotalAfterSync: number;
		syncCompleted: () => any;
	};
	extraHeaderComponent?: () => any;
	rowDefinitions?: ReactableRowDef<T>;
	clickable?: boolean;
};
type State = {
	query: string;
	paginationSettings: PaginationSettings;
	isSearching: boolean;
	searchResult: Pageable<Record<string, any>> | null | undefined;
	showSyncSuccess: boolean;
};
const REPEAT_SYNC_TIMEOUT = 3000;
const SEARCH_DEBOUNCE = 300;
export default class FilterTable extends React.Component<Props<any>, State> {
	mounted = false;

	constructor(props: Props<any>) {
		super(props);
		this.state = {
			query: '',
			paginationSettings: this.getInitialPaginationSettings(),
			isSearching: false,
			searchResult: null,
			showSyncSuccess: false
		};
	}

	componentDidMount() {
		this.mounted = true;
		this.triggerSearch(this.state.query, this.state.paginationSettings);
	}

	componentWillUnmount() {
		this.mounted = false;
	}

	componentDidUpdate(prevProps: Props<any>, prevState: State) {
		const {onUpdate, updateTrigger} = this.props;

		if (updateTrigger && updateTrigger !== prevProps.updateTrigger) {
			this.handleChangePage(0);
			this.handleSyncProcess();
		}

		const {searchResult} = this.state;

		if (onUpdate && searchResult && searchResult !== prevState.searchResult) {
			onUpdate(this.state.query, searchResult);
		}

		if (
			this.props.withSync &&
			this.props.withSync.itemsToSync &&
			!this.isNotSynced &&
			!this.state.showSyncSuccess
		) {
			this.clearSync(true);
		}
	}

	getInitialPaginationSettings = (): PaginationSettings => {
		const {columnDefinitions, pageSize} = this.props;
		const defaultSortColumn = columnDefinitions.find((cd) => cd.sortable) || columnDefinitions[0];
		return {
			page: 0,
			size: pageSize,
			sort: {
				property: defaultSortColumn.name,
				direction: defaultSortColumn.defaultSortDirection || 'ASC'
			}
		};
	};

	get isNotSynced() {
		const {withSync} = this.props;
		const {searchResult} = this.state;
		return !!(
			withSync &&
			withSync.itemsToSync &&
			withSync.expectedTotalAfterSync !== undefined &&
			searchResult &&
			withSync.expectedTotalAfterSync !== searchResult.totalElements
		);
	}

	handleSyncProcess = (repeat = 3) => {
		if (this.isNotSynced) {
			setTimeout(() => this.repeatSearch(repeat), REPEAT_SYNC_TIMEOUT);
		}
	};
	clearSync = (showCompleted: boolean) => {
		const {withSync} = this.props;

		if (withSync) {
			withSync.syncCompleted();
		}

		if (showCompleted) {
			this.setState({
				showSyncSuccess: true
			});
			setTimeout(
				() =>
					this.setState({
						showSyncSuccess: false
					}),
				REPEAT_SYNC_TIMEOUT
			);
		}
	};
	repeatSearch = (repeat: number) => {
		if (this.isNotSynced) {
			this.triggerSearch(this.state.query, this.state.paginationSettings);
		}

		if (repeat > 0) {
			this.handleSyncProcess(repeat - 1);
		} else {
			this.clearSync(false);
		}
	};
	triggerSearch = (query: string, paginationSettings: PaginationSettings) => {
		this.setState({
			isSearching: true
		});
		this.props.searchConfig
			.search(query, paginationSettings)
			.then((searchResult) => {
				if (this.mounted) {
					this.setState({
						searchResult
					});
				}
			})
			.finally(() => {
				if (this.mounted) {
					this.setState({
						isSearching: false
					});
				}
			});
	};
	handleChangeQuery = debounce((query: string) => {
		const page = 0;
		const paginationSettings = {...this.state.paginationSettings, page};
		this.setState(
			{
				query,
				paginationSettings
			},
			() => this.triggerSearch(query, this.state.paginationSettings)
		);
	}, SEARCH_DEBOUNCE);
	handleChangePage = (page: number) => {
		const paginationSettings = {...this.state.paginationSettings, page, size: this.props.pageSize};
		this.setState({
			paginationSettings
		});
		this.triggerSearch(this.state.query, paginationSettings);
	};
	handleSort = (property: string, direction: SortDirection) => {
		const paginationSettings = {
			...this.state.paginationSettings,
			sort: {
				property,
				direction
			}
		};
		this.setState({
			paginationSettings
		});
		this.triggerSearch(this.state.query, paginationSettings);
	};

	render() {
		const {
			searchContext,
			searchConfig,
			columnDefinitions,
			rowDefinitions,
			clickable,
			uniqueItemKey,
			withSync,
			extraHeaderComponent: ExtraHeaderComponent
		} = this.props;
		const {query, paginationSettings, isSearching, searchResult} = this.state;
		let syncedItems = 0;

		if (this.isNotSynced && withSync && searchResult && searchResult.totalElements) {
			syncedItems = withSync.itemsToSync - Math.abs(withSync.expectedTotalAfterSync - searchResult.totalElements);
		}

		return (
			<span>
				<span className="di searchL">
					<span>
						<Search
							context={searchContext}
							config={searchConfig}
							onChange={this.handleChangeQuery}
							placeholder={{
								id: 'components.filtertable.searchPlaceholder',
								defaultValue: 'Filter'
							}}
						/>
						{query.length > 0 && !isSearching && searchResult && (
							<span className="search-result">
								<Trans i18nKey="components.filtertable.elementsFound">
									<>
										{{
											elements: searchResult.totalElements
										}}{' '}
										found
									</>
								</Trans>
							</span>
						)}
					</span>
					{this.isNotSynced && withSync && (
						<span>
							<Trans i18nKey="components.filtertable.syncInProgress">Sync in progress</Trans>
							&nbsp;
							<progress max={withSync.itemsToSync} value={syncedItems} />
						</span>
					)}
					{!this.isNotSynced && withSync && this.state.showSyncSuccess && (
						<span className="di sync-completed">
							<Trans i18nKey="components.filtertable.syncCompleted">Sync completed!</Trans>
						</span>
					)}
					{!!ExtraHeaderComponent && <ExtraHeaderComponent />}
				</span>
				<Reactable
					columnDefs={columnDefinitions}
					rowDef={rowDefinitions}
					uniqueItemKey={uniqueItemKey}
					paginationSettings={paginationSettings}
					onChangePage={this.handleChangePage}
					onSort={this.handleSort}
					pageable={searchResult}
					isLoading={isSearching && !this.isNotSynced}
					clickable={clickable}
				/>
			</span>
		);
	}
}
